import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { Entity } from "@common/types/entity";
import { FinancialAccount } from "@common/types/financialAccount";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { KickLogo } from "@design-system/KickLogo/KickLogo";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { getSubaccountDisplayName } from "@design-system/Subaccount/utils";
import { AccountPickerIcon } from "@components/financialAccount/AccountPickerItem";
import css from "./AggregatedAccountsFilter.module.scss";

export const KICK_VALUE = "kick";
export const NO_ACCOUNT_VALUE = "no-account";

export const entityValue = (entityId: number) => `entity:${entityId}`;
export const accountValue = (accountId: number) => `account:${accountId}`;
export const subaccountValue = (subaccountId: string) =>
    `subaccount:${subaccountId}`;

/** What ticking one row of the account filter stands for. */
export type AccountFilterRow =
    | { kind: "kick" }
    | { kind: "noAccount" }
    | { kind: "entity"; accountIds: number[] }
    | { kind: "account"; account: FinancialAccount }
    | { kind: "subaccount"; subaccountId: string };

export interface EntityAccounts {
    entity: Entity;
    accounts: FinancialAccount[];
}

const toAccountItem = (account: FinancialAccount): PickerItemData<string> => {
    const mask = account.accountNumberMask;
    const lastFour = mask ? getAnonymousAccountNumber(mask, 0) : undefined;
    return {
        value: accountValue(account.id),
        label: lastFour ?? account.name ?? "Account",
        content:
            lastFour === undefined ? undefined : (
                <span className={css.accountNumber}>
                    <span className={css.accountMask}>••</span>
                    {lastFour}
                </span>
            ),
        tooltip:
            lastFour !== undefined && account.name ? account.name : undefined,
        leading: <AccountPickerIcon account={account} />,
        children: (account.subaccounts ?? []).map((subaccount) => ({
            value: subaccountValue(subaccount.id),
            label: getSubaccountDisplayName(subaccount),
        })),
    };
};

const entityRows = ({
    entity,
    accounts,
}: EntityAccounts): Array<[string, AccountFilterRow]> => [
    [
        entityValue(entity.id),
        { kind: "entity", accountIds: accounts.map(({ id }) => id) },
    ],
    ...accounts.flatMap((account): Array<[string, AccountFilterRow]> => [
        [accountValue(account.id), { kind: "account", account }],
        ...(account.subaccounts ?? []).map(
            (subaccount): [string, AccountFilterRow] => [
                subaccountValue(subaccount.id),
                { kind: "subaccount", subaccountId: subaccount.id },
            ],
        ),
    ]),
];

/**
 * The account filter's rows: the optional "Kick" row, each entity with its
 * accounts (and their subaccounts), and the optional "No account" row, plus
 * what each rendered row value stands for.
 */
export const buildAccountFilterRows = ({
    entityAccounts,
    shouldShowKickRow,
    shouldShowNoAccountRow,
}: {
    entityAccounts: EntityAccounts[];
    shouldShowKickRow: boolean;
    shouldShowNoAccountRow: boolean;
}) => {
    const items: Array<PickerItemData<string>> = [
        ...(shouldShowKickRow
            ? [
                  {
                      value: KICK_VALUE,
                      label: "Kick",
                      leading: <KickLogo variant="icon" size="xs" />,
                  },
              ]
            : []),
        ...entityAccounts.map(({ entity, accounts }) => ({
            value: entityValue(entity.id),
            label: entity.name ?? "",
            leading: <EntityAvatar entity={entity} size="xs" aria-hidden />,
            children: accounts.map(toAccountItem),
        })),
        ...(shouldShowNoAccountRow
            ? [{ value: NO_ACCOUNT_VALUE, label: "No account" }]
            : []),
    ];
    const kickRows: Array<[string, AccountFilterRow]> = shouldShowKickRow
        ? [[KICK_VALUE, { kind: "kick" }]]
        : [];
    const noAccountRows: Array<[string, AccountFilterRow]> =
        shouldShowNoAccountRow
            ? [[NO_ACCOUNT_VALUE, { kind: "noAccount" }]]
            : [];
    const rowByValue = new Map<string, AccountFilterRow>([
        ...kickRows,
        ...entityAccounts.flatMap(entityRows),
        ...noAccountRows,
    ]);
    return { items, rowByValue };
};
